import React, { useMemo } from "react";
import { TableCell, TableRow, Tooltip, Typography, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { selectStrategies, selectFunds } from "../../../Redux/Reducers/selectors";
import { getSignColor } from "../../PerformanceDashboard/utility";
import {
  MarketTooltip,
  TradesTooltip,
  getTradesTooltipValues,
} from "../../Portfolio/styles/positionTableTooltips";
import {
  getRowDisplayValues,
  getSideColor,
} from "../../Portfolio/utils/portfolioExport";
import {
  V2_POSITION_ROW_HEIGHT,
  getBodyCellSx,
  getRowHoverSx,
  v2TooltipSlotProps,
} from "./positionTableV2Styles";

const EmptyCell = ({ theme }) => (
  <TableCell align="right" sx={getBodyCellSx(theme)}>
    {" "}
  </TableCell>
);

const PositionTableV2 = React.memo(({ row, fundVal, strategyVal, positionType = "long", expanded = false }) => {
  const isLong = positionType === "long";
  const theme = useTheme();
  const funds = useSelector(selectFunds);
  const strategies = useSelector(selectStrategies);

  const display = useMemo(
    () => getRowDisplayValues(row, isLong),
    [row, isLong]
  );
  const trades = useMemo(
    () => getTradesTooltipValues(row, isLong),
    [row, isLong]
  );

  const accent = getSignColor(display.plRaw ?? 0, theme);

  const handlePosition = () => {
    if (
      !fundVal ||
      !strategyVal ||
      fundVal.toUpperCase() === "ALL" ||
      strategyVal.toUpperCase() === "ALL"
    ) {
      return;
    }
    funds?.find((i) => i.abbreviation == fundVal);
    strategies?.find((i) => i.name == strategyVal);
  };

  const colorFor = (raw, isLongSide, opts) =>
    getSideColor(theme, raw, isLongSide, opts);

  return (
    <TableRow
      hover
      onClick={handlePosition}
      sx={{
        height: V2_POSITION_ROW_HEIGHT,
        maxHeight: V2_POSITION_ROW_HEIGHT,
        ...getRowHoverSx(theme),
      }}
    >
      <TradesTooltip
        enabled={trades.size.changed}
        title="SIZE"
        yest={trades.size.yest}
        today={trades.size.today}
      >
        <TableCell
          align="right"
          sx={{
            ...getBodyCellSx(theme),
            borderLeft: `3px solid ${accent}`,
          }}
        >
          {display.size}
        </TableCell>
      </TradesTooltip>

      <TradesTooltip
        enabled={trades.shares.changed}
        title="SHARES"
        yest={trades.shares.yest}
        today={trades.shares.today}
      >
        <TableCell align="right" sx={getBodyCellSx(theme)}>
          {display.shares}
        </TableCell>
      </TradesTooltip>

      <TableCell
        align="left"
        sx={getBodyCellSx(theme, { sticky: true })}
      >
        <Tooltip
          arrow
          placement="right"
          title={display.ticker}
          slotProps={v2TooltipSlotProps(theme)}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.92rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {display.ticker}
          </Typography>
        </Tooltip>
      </TableCell>

      <MarketTooltip dataSource={row?.dataSource} fetched={row?.fetched}>
        <TableCell
          align="right"
          sx={getBodyCellSx(theme, { marketValid: row?.valid })}
        >
          {display.last}
        </TableCell>
      </MarketTooltip>

      <MarketTooltip dataSource={row?.dataSource} fetched={row?.fetched}>
        <TableCell
          align="right"
          sx={getBodyCellSx(theme, { marketValid: row?.valid })}
        >
          {display.current}
        </TableCell>
      </MarketTooltip>

      {display.showChg ? (
        <TableCell
          align="right"
          sx={getBodyCellSx(theme, {
            color: colorFor(display.chRaw, isLong),
          })}
        >
          {display.ch}
        </TableCell>
      ) : (
        <EmptyCell theme={theme} />
      )}

      {display.showPl ? (
        <TradesTooltip
          enabled={trades.pl.changed}
          title="P(L)"
          yest={trades.pl.yest}
          today={trades.pl.today}
        >
          <TableCell
            align="right"
            sx={getBodyCellSx(theme, {
              color: colorFor(display.plRaw, true),
            })}
          >
            {display.pl}
          </TableCell>
        </TradesTooltip>
      ) : (
        <EmptyCell theme={theme} />
      )}

      {display.showChPct ? (
        <TableCell
          align="right"
          sx={getBodyCellSx(theme, {
            color: colorFor(display.chPctRaw, isLong),
          })}
        >
          {display.chPct}
        </TableCell>
      ) : (
        <EmptyCell theme={theme} />
      )}

      {expanded ? (
        <>
          <TableCell align="right" sx={getBodyCellSx(theme)}>
            {display.plAvgCost}
          </TableCell>
          <TableCell align="right" sx={getBodyCellSx(theme)}>
            {display.pctAvgCost}
          </TableCell>
        </>
      ) : null}

      <TradesTooltip
        enabled={trades.cost.changed}
        title="AVG COST"
        yest={trades.cost.yest}
        today={trades.cost.today}
      >
        <TableCell align="right" sx={getBodyCellSx(theme)}>
          {display.cost}
        </TableCell>
      </TradesTooltip>

      <TableCell
        align="right"
        sx={getBodyCellSx(theme, {
          color: colorFor(display.tdgRaw, true, { treatZeroAsNeutral: true }),
        })}
      >
        {display.tdg}
      </TableCell>
    </TableRow>
  );
});

PositionTableV2.displayName = "PositionTableV2";

export default PositionTableV2;
